import {
  CAPABILITY_TOKEN_AUDIENCE_EXECUTION_ENGINE,
  type CapabilityScope,
} from "@repo/capability-token";
import { useSyncExternalStore } from "react";
import { getPlatformApiBaseUrl } from "~/lib/env";
import { setVmPreference } from "~/lib/auth/vm-preference";
import {
  normalizeVmId,
  parseVmId,
  type VmPreference,
} from "~/lib/auth/vm-types";

const SYNTHETIC_DEV_AUTH_STORAGE_KEY = "glider:webapp-v2:synthetic-dev-auth:v1";
const SYNTHETIC_DEV_EVM_SIGNER_SESSION_STORAGE_KEY =
  "glider:webapp-v2:synthetic-dev-evm-signer:v1";
const SIWE_AUTH_STORAGE_KEY = "glider:webapp-v2:siwe-auth:v1";
const SIWE_EVM_SIGNER_SESSION_STORAGE_KEY =
  "glider:webapp-v2:siwe-evm-signer:v1";
const QUERY_PARAM_WALLET = "devAuthWallet";
const QUERY_PARAM_SECONDARY_WALLET = "devAuthSecondaryWallet";
const QUERY_PARAM_TARGET = "devAuthTarget";
const QUERY_PARAM_USER_ID = "devAuthUserId";
const QUERY_PARAM_VM = "devAuthVm";
const DEV_AUTH_TOKEN_EXPIRY_CLOCK_SKEW_MS = 30_000;
const SIWE_ACCESS_TOKEN_EXPIRY_CLOCK_SKEW_MS = 30_000;
const PRIVY_ACCESS_TOKEN_CACHE_FALLBACK_TTL_MS = 30_000;
const PRIVY_ACCESS_TOKEN_CACHE_NEGATIVE_TTL_MS = 10_000;
const PRIVY_ACCESS_TOKEN_EXPIRY_CLOCK_SKEW_MS = 30_000;
const PRIVY_IDENTITY_TOKEN_CACHE_TTL_MS = 30_000;
const PRIVY_IDENTITY_TOKEN_NEGATIVE_CACHE_TTL_MS = 10_000;
const CAPABILITY_TOKEN_EXPIRY_CLOCK_SKEW_MS = 30_000;
const CAPABILITY_TOKEN_CACHE_FALLBACK_TTL_MS = 30_000;
const CAPABILITY_TOKEN_NEGATIVE_CACHE_TTL_MS = 60_000;

export const BROWSE_SCOPES = [
  "quote:read",
  "feed:read",
] as const satisfies readonly CapabilityScope[];

export type ExecuteCapabilityScope = Extract<
  CapabilityScope,
  `execute:${string}`
>;

export type AppAuthCapabilityTokenRequest = {
  readonly scopes?: readonly CapabilityScope[];
  readonly portfolioIds?: readonly string[];
  readonly ttlSeconds?: number;
};

export type SyntheticDevAuthTarget = "local" | "staging";

export interface SyntheticDevAuthProfile {
  devAuthToken: string | null;
  evmPrivateKey: string | null;
  secondaryWalletAddress: string | null;
  target: SyntheticDevAuthTarget;
  userId: string | null;
  vmPreference: VmPreference;
  walletAddress: string;
}

export interface SiweAuthProfile {
  accessToken: string | null;
  chainId: number;
  expiresAt: string | null;
  refreshToken: string | null;
  sessionId: string | null;
  walletAddress: string;
}

export type SiweAuthProfilePatch = Partial<SiweAuthProfile>;

export interface SiweEvmSignerProfile {
  chainId: number;
  evmPrivateKey: string;
  sessionId: string | null;
  walletAddress: string;
}

export type SyntheticDevAuthProfilePatch = Partial<
  Omit<SyntheticDevAuthProfile, "walletAddress">
> & {
  walletAddress?: string;
};

export interface AppAuthTransportSnapshot {
  mode: "guest" | "privy" | "siwe" | "synthetic-local" | "synthetic-staging";
  devAuthToken: string | null;
  evmAddress: string | null;
  siweAccessToken?: string | null;
  solanaAddress: string | null;
  userId: string | null;
  walletAddress: string | null;
}

type Listener = () => void;
type PrivyAccessTokenGetter = () => Promise<string | null>;
type PrivyIdentityTokenGetter = () => Promise<string | null>;
type PrivyAccessTokenGetterWaiter = (
  getter: PrivyAccessTokenGetter | null,
) => void;
type CapabilityTokenFetchResult = {
  readonly expiresAt: string | null;
  readonly negativeCache: boolean;
  readonly token: string | null;
};

type NormalizedCapabilityTokenRequest = {
  readonly portfolioIds: readonly string[] | undefined;
  readonly scopes: readonly CapabilityScope[];
  readonly ttlSeconds: number | undefined;
};

type CapabilityTokenCacheEntry = {
  expiresAtMs: number;
  inFlight: Promise<CapabilityTokenFetchResult> | null;
  token: string | null;
};

const PRIVY_ACCESS_TOKEN_GETTER_WAIT_TIMEOUT_MS = 1_000;

export const GUEST_TRANSPORT_SNAPSHOT: AppAuthTransportSnapshot = {
  mode: "guest",
  devAuthToken: null,
  evmAddress: null,
  siweAccessToken: null,
  solanaAddress: null,
  userId: null,
  walletAddress: null,
};

let syntheticProfileInitialized = false;
let syntheticProfile: SyntheticDevAuthProfile | null = null;
let siweProfileInitialized = false;
let siweProfile: SiweAuthProfile | null = null;
let siweEvmSignerProfileInitialized = false;
let siweEvmSignerProfile: SiweEvmSignerProfile | null = null;
let transportSnapshot: AppAuthTransportSnapshot = GUEST_TRANSPORT_SNAPSHOT;
let privyAccessTokenGetter: PrivyAccessTokenGetter | null = null;
let privyIdentityTokenGetter: PrivyIdentityTokenGetter | null = null;
let privyAccessTokenCache: {
  cacheKey: string | null;
  expiresAtMs: number;
  inFlight: Promise<string | null> | null;
  inFlightCacheKey: string | null;
  token: string | null;
} = {
  cacheKey: null,
  expiresAtMs: 0,
  inFlight: null,
  inFlightCacheKey: null,
  token: null,
};
let privyIdentityTokenCache: {
  accessToken: string | null;
  expiresAtMs: number;
  inFlight: Promise<string | null> | null;
  inFlightAccessToken: string | null;
  token: string | null;
} = {
  accessToken: null,
  expiresAtMs: 0,
  inFlight: null,
  inFlightAccessToken: null,
  token: null,
};
const capabilityTokenCache = new Map<string, CapabilityTokenCacheEntry>();

const syntheticProfileListeners = new Set<Listener>();
const siweProfileListeners = new Set<Listener>();
const siweEvmSignerProfileListeners = new Set<Listener>();
const transportSnapshotListeners = new Set<Listener>();
const privyAccessTokenGetterWaiters = new Set<PrivyAccessTokenGetterWaiter>();

function emitListeners(listeners: Set<Listener>): void {
  for (const listener of listeners) {
    listener();
  }
}

function subscribeToSet(
  listeners: Set<Listener>,
  listener: Listener,
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function resolvePrivyAccessTokenGetterWaiters(
  getter: PrivyAccessTokenGetter | null,
): void {
  const waiters = Array.from(privyAccessTokenGetterWaiters);
  for (const waiter of waiters) {
    waiter(getter);
  }
}

function waitForPrivyAccessTokenGetter(): Promise<PrivyAccessTokenGetter | null> {
  if (privyAccessTokenGetter) {
    return Promise.resolve(privyAccessTokenGetter);
  }

  return new Promise((resolve) => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const resolveOnce: PrivyAccessTokenGetterWaiter = (getter) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      privyAccessTokenGetterWaiters.delete(resolveOnce);
      resolve(getter);
    };

    timeoutId = setTimeout(() => {
      resolveOnce(null);
    }, PRIVY_ACCESS_TOKEN_GETTER_WAIT_TIMEOUT_MS);

    privyAccessTokenGetterWaiters.add(resolveOnce);
  });
}

function isSyntheticDevAuthRuntimeEnabled(): boolean {
  return import.meta.env.DEV || import.meta.env.MODE === "test";
}

function warnSyntheticDevAuthRuntimeDisabled(): void {
  console.warn(
    "Synthetic dev auth is disabled outside development and test runtimes.",
  );
}

function isSiweAuthRuntimeEnabled(): boolean {
  return (
    import.meta.env.DEV ||
    import.meta.env.MODE === "test" ||
    import.meta.env.VITE_ENABLE_WALLETLESS_SIWE_E2E === "1"
  );
}

function normalizeNonEmptyString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const [, payload] = token.split(".");
  if (!payload || typeof globalThis.atob !== "function") {
    return null;
  }

  const base64 = payload.replace(/-/gu, "+").replace(/_/gu, "/");
  const paddingLength = (4 - (base64.length % 4)) % 4;

  try {
    const decoded = globalThis.atob(`${base64}${"=".repeat(paddingLength)}`);
    const parsed: unknown = JSON.parse(decoded);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return null;
    }

    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
}

function isDevAuthJwtExpired(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload) {
    return false;
  }

  const exp = payload.exp;
  if (typeof exp !== "number" || !Number.isFinite(exp)) {
    return false;
  }

  return exp * 1_000 <= Date.now() + DEV_AUTH_TOKEN_EXPIRY_CLOCK_SKEW_MS;
}

function readJwtExpirationMs(token: string): number | null {
  const payload = decodeJwtPayload(token);
  if (!payload) {
    return null;
  }

  const exp = payload.exp;
  return typeof exp === "number" && Number.isFinite(exp) ? exp * 1_000 : null;
}

function normalizeDevAuthToken(value: unknown): string | null {
  const token = normalizeNonEmptyString(value);
  if (!token) {
    return null;
  }

  return isDevAuthJwtExpired(token) ? null : token;
}

function isJwtExpired(token: string, clockSkewMs: number): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload) {
    return false;
  }

  const exp = payload.exp;
  if (typeof exp !== "number" || !Number.isFinite(exp)) {
    return false;
  }

  return exp * 1_000 <= Date.now() + clockSkewMs;
}

function normalizeSiweAccessToken(value: unknown): string | null {
  const token = normalizeNonEmptyString(value);
  if (!token) {
    return null;
  }

  return isJwtExpired(token, SIWE_ACCESS_TOKEN_EXPIRY_CLOCK_SKEW_MS)
    ? null
    : token;
}

function normalizeIsoDateString(value: unknown): string | null {
  const rawValue = normalizeNonEmptyString(value);
  if (!rawValue) {
    return null;
  }

  const parsed = new Date(rawValue);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed.toISOString();
}

function normalizePositiveInteger(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isInteger(value) || value <= 0) {
    return null;
  }

  return value;
}

function getPrivyAccessTokenCacheKey(
  snapshot: AppAuthTransportSnapshot,
): string {
  return [
    snapshot.userId ?? "",
    snapshot.walletAddress ?? "",
    snapshot.evmAddress ?? "",
    snapshot.solanaAddress ?? "",
  ].join("\n");
}

function getPrivyAccessTokenCacheExpiryMs(input: {
  nowMs: number;
  token: string | null;
}): number {
  if (!input.token) {
    return input.nowMs + PRIVY_ACCESS_TOKEN_CACHE_NEGATIVE_TTL_MS;
  }

  const tokenExpiresAtMs = readJwtExpirationMs(input.token);
  if (tokenExpiresAtMs !== null) {
    return Math.max(
      input.nowMs,
      tokenExpiresAtMs - PRIVY_ACCESS_TOKEN_EXPIRY_CLOCK_SKEW_MS,
    );
  }

  return input.nowMs + PRIVY_ACCESS_TOKEN_CACHE_FALLBACK_TTL_MS;
}

async function readPrivyAccessToken(
  getter: PrivyAccessTokenGetter | null,
  snapshot: AppAuthTransportSnapshot,
): Promise<string | null> {
  if (!getter) {
    return null;
  }

  const cacheKey = getPrivyAccessTokenCacheKey(snapshot);
  const now = Date.now();
  if (
    privyAccessTokenCache.cacheKey === cacheKey &&
    privyAccessTokenCache.expiresAtMs > now
  ) {
    return privyAccessTokenCache.token;
  }

  if (
    privyAccessTokenCache.inFlight &&
    privyAccessTokenCache.inFlightCacheKey === cacheKey
  ) {
    return await privyAccessTokenCache.inFlight;
  }

  const tokenPromise = (async () => {
    try {
      return normalizeNonEmptyString(await getter());
    } catch {
      return null;
    }
  })();
  privyAccessTokenCache.inFlight = tokenPromise;
  privyAccessTokenCache.inFlightCacheKey = cacheKey;

  try {
    const token = await tokenPromise;
    if (
      privyAccessTokenCache.inFlight === tokenPromise &&
      privyAccessTokenCache.inFlightCacheKey === cacheKey
    ) {
      privyAccessTokenCache = {
        cacheKey,
        expiresAtMs: getPrivyAccessTokenCacheExpiryMs({
          nowMs: Date.now(),
          token,
        }),
        inFlight: tokenPromise,
        inFlightCacheKey: cacheKey,
        token,
      };
    }
    return token;
  } finally {
    if (privyAccessTokenCache.inFlight === tokenPromise) {
      privyAccessTokenCache = {
        ...privyAccessTokenCache,
        inFlight: null,
        inFlightCacheKey: null,
      };
    }
  }
}

function getCapabilityTokenSnapshotCacheKey(
  snapshot: AppAuthTransportSnapshot,
): string {
  return [
    snapshot.mode,
    snapshot.userId ?? "",
    snapshot.walletAddress ?? "",
    snapshot.evmAddress ?? "",
    snapshot.solanaAddress ?? "",
    snapshot.devAuthToken ?? "",
  ].join("\n");
}

function normalizeCapabilityTokenRequest(
  request: AppAuthCapabilityTokenRequest | undefined,
): NormalizedCapabilityTokenRequest {
  const scopes = Array.from(new Set(request?.scopes ?? BROWSE_SCOPES)).sort();
  const portfolioIds =
    request?.portfolioIds === undefined
      ? undefined
      : Array.from(
          new Set(
            request.portfolioIds
              .map((portfolioId) => portfolioId.trim())
              .filter((portfolioId) => portfolioId.length > 0),
          ),
        ).sort();

  return {
    portfolioIds,
    scopes,
    ttlSeconds: request?.ttlSeconds,
  };
}

function getCapabilityTokenCacheKey(
  snapshot: AppAuthTransportSnapshot,
  request: NormalizedCapabilityTokenRequest,
): string {
  return [
    getCapabilityTokenSnapshotCacheKey(snapshot),
    request.scopes.join(","),
    request.portfolioIds === undefined
      ? "<portfolio-ids-omitted>"
      : request.portfolioIds.join(","),
  ].join("\n");
}

function getCapabilityTokenCacheExpiryMs(input: {
  expiresAt: string | null;
  negativeCache: boolean;
  nowMs: number;
  token: string | null;
}): number {
  if (!input.token) {
    return input.negativeCache
      ? input.nowMs + CAPABILITY_TOKEN_NEGATIVE_CACHE_TTL_MS
      : input.nowMs;
  }

  const responseExpiresAtMs = input.expiresAt
    ? Date.parse(input.expiresAt)
    : Number.NaN;
  const tokenExpiresAtMs = Number.isFinite(responseExpiresAtMs)
    ? responseExpiresAtMs
    : readJwtExpirationMs(input.token);
  if (tokenExpiresAtMs !== null && Number.isFinite(tokenExpiresAtMs)) {
    return Math.max(
      input.nowMs,
      tokenExpiresAtMs - CAPABILITY_TOKEN_EXPIRY_CLOCK_SKEW_MS,
    );
  }

  return input.nowMs + CAPABILITY_TOKEN_CACHE_FALLBACK_TTL_MS;
}

function isCapabilityMintResponse(
  value: unknown,
): value is { readonly expiresAt: string; readonly token: string } {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.token === "string" &&
    candidate.token.trim().length > 0 &&
    typeof candidate.expiresAt === "string" &&
    candidate.expiresAt.trim().length > 0
  );
}

async function fetchCapabilityTokenForSnapshot(
  snapshot: AppAuthTransportSnapshot,
  request: NormalizedCapabilityTokenRequest,
): Promise<CapabilityTokenFetchResult> {
  if (snapshot.mode === "guest") {
    return { expiresAt: null, negativeCache: false, token: null };
  }

  const authHeaders = await getAppAuthTransportAuthHeadersForSnapshot(snapshot);
  if (Object.keys(authHeaders).length === 0) {
    return { expiresAt: null, negativeCache: false, token: null };
  }

  let response: Response;
  try {
    const body: {
      audience: string;
      scopes: readonly CapabilityScope[];
      portfolioIds?: readonly string[];
      ttlSeconds?: number;
    } = {
      audience: CAPABILITY_TOKEN_AUDIENCE_EXECUTION_ENGINE,
      scopes: request.scopes,
    };
    if (request.portfolioIds !== undefined) {
      body.portfolioIds = request.portfolioIds;
    }
    if (request.ttlSeconds !== undefined) {
      body.ttlSeconds = request.ttlSeconds;
    }

    response = await fetch(
      `${getPlatformApiBaseUrl().replace(/\/+$/u, "")}/v1/me/capabilities`,
      {
        body: JSON.stringify(body),
        credentials: "include",
        headers: {
          ...authHeaders,
          "content-type": "application/json",
        },
        method: "POST",
      },
    );
  } catch {
    return { expiresAt: null, negativeCache: false, token: null };
  }

  if (response.status === 403 || response.status === 404) {
    return { expiresAt: null, negativeCache: true, token: null };
  }

  if (!response.ok) {
    return { expiresAt: null, negativeCache: false, token: null };
  }

  const raw = await response.json().catch(() => null);
  if (!isCapabilityMintResponse(raw)) {
    return { expiresAt: null, negativeCache: false, token: null };
  }

  return {
    expiresAt: raw.expiresAt,
    negativeCache: false,
    token: raw.token.trim(),
  };
}

async function readCapabilityTokenForSnapshot(
  snapshot: AppAuthTransportSnapshot,
  request: NormalizedCapabilityTokenRequest,
  options: { readonly forceRefresh?: boolean } = {},
): Promise<string | null> {
  const cacheKey = getCapabilityTokenCacheKey(snapshot, request);
  const cacheEntry = capabilityTokenCache.get(cacheKey);
  const now = Date.now();
  if (!options.forceRefresh && cacheEntry && cacheEntry.expiresAtMs > now) {
    return cacheEntry.token;
  }

  if (cacheEntry?.inFlight) {
    return (await cacheEntry.inFlight).token;
  }

  const tokenPromise = fetchCapabilityTokenForSnapshot(snapshot, request);
  capabilityTokenCache.set(cacheKey, {
    expiresAtMs: cacheEntry?.expiresAtMs ?? 0,
    inFlight: tokenPromise,
    token: cacheEntry?.token ?? null,
  });

  try {
    const result = await tokenPromise;
    const token = result.token;
    const currentEntry = capabilityTokenCache.get(cacheKey);
    if (currentEntry?.inFlight === tokenPromise) {
      capabilityTokenCache.set(cacheKey, {
        expiresAtMs: getCapabilityTokenCacheExpiryMs({
          expiresAt: result.expiresAt,
          negativeCache: result.negativeCache,
          nowMs: Date.now(),
          token,
        }),
        inFlight: tokenPromise,
        token,
      });
    }
    return token;
  } finally {
    const currentEntry = capabilityTokenCache.get(cacheKey);
    if (currentEntry?.inFlight === tokenPromise) {
      capabilityTokenCache.set(cacheKey, {
        ...currentEntry,
        inFlight: null,
      });
    }
  }
}

function clearCapabilityTokenCache(): void {
  capabilityTokenCache.clear();
}

function clearPrivyAccessTokenCache(): void {
  privyAccessTokenCache = {
    cacheKey: null,
    expiresAtMs: 0,
    inFlight: null,
    inFlightCacheKey: null,
    token: null,
  };
}

async function readPrivyIdentityTokenBestEffort(
  getter: PrivyIdentityTokenGetter | null,
  accessToken: string | null,
): Promise<string | null> {
  const normalizedAccessToken = normalizeNonEmptyString(accessToken);
  if (!getter || !normalizedAccessToken) {
    return null;
  }

  const now = Date.now();
  if (
    privyIdentityTokenCache.accessToken === normalizedAccessToken &&
    privyIdentityTokenCache.expiresAtMs > now
  ) {
    return privyIdentityTokenCache.token;
  }

  if (
    privyIdentityTokenCache.inFlight &&
    privyIdentityTokenCache.inFlightAccessToken === normalizedAccessToken
  ) {
    return await privyIdentityTokenCache.inFlight;
  }

  const tokenPromise = (async () => {
    try {
      return normalizeNonEmptyString(await getter());
    } catch {
      return null;
    }
  })();

  privyIdentityTokenCache.inFlight = tokenPromise;
  privyIdentityTokenCache.inFlightAccessToken = normalizedAccessToken;

  try {
    const identityToken = await tokenPromise;
    if (
      privyIdentityTokenCache.inFlight === tokenPromise &&
      privyIdentityTokenCache.inFlightAccessToken === normalizedAccessToken
    ) {
      privyIdentityTokenCache = {
        accessToken: normalizedAccessToken,
        expiresAtMs:
          Date.now() +
          (identityToken
            ? PRIVY_IDENTITY_TOKEN_CACHE_TTL_MS
            : PRIVY_IDENTITY_TOKEN_NEGATIVE_CACHE_TTL_MS),
        inFlight: tokenPromise,
        inFlightAccessToken: normalizedAccessToken,
        token: identityToken,
      };
    }
    return identityToken;
  } finally {
    if (privyIdentityTokenCache.inFlight === tokenPromise) {
      privyIdentityTokenCache = {
        ...privyIdentityTokenCache,
        inFlight: null,
        inFlightAccessToken: null,
      };
    }
  }
}

export function clearPrivyIdentityTokenCacheForTests(): void {
  privyIdentityTokenCache = {
    accessToken: null,
    expiresAtMs: 0,
    inFlight: null,
    inFlightAccessToken: null,
    token: null,
  };
}

/**
 * Forces the next identity-token read to skip the cache — used right after
 * the user links a new email, when the server must see a token minted AFTER
 * the link (the Crossmint KYC email rides the `privy-id-token` header; a
 * cached pre-link token keeps failing with EMAIL_REQUIRED). Expires the
 * cached value but keeps any in-flight fetch: its result is already coming
 * from the network, so joining it is still "fresh enough", and clobbering it
 * would race its completion handler. Best-effort: returns null when
 * unauthenticated or the refresh failed — the request then goes out without
 * a fresh header and the server falls back to Privy's API (always fresh).
 *
 * Reads through the registered getters (never `@privy-io/react-auth`
 * directly): this module sits in the eager entry graph, and a static Privy
 * import here would drag the SDK into the initial bundle, undoing the
 * lazy-privy-app-provider code split. The flows that call this only run
 * inside the Privy runtime, where the getters are registered.
 */
export async function refreshPrivyIdentityToken(): Promise<string | null> {
  // Expire the cached identity token up front so even a failed access-token
  // read leaves the next transport request re-resolving instead of reusing
  // the stale pre-link token.
  privyIdentityTokenCache = {
    ...privyIdentityTokenCache,
    accessToken: null,
    expiresAtMs: 0,
    token: null,
  };
  const getter = privyAccessTokenGetter;
  if (!getter) {
    return null;
  }
  const accessToken = normalizeNonEmptyString(await getter().catch(() => null));
  if (!accessToken) {
    return null;
  }
  return await readPrivyIdentityTokenBestEffort(
    privyIdentityTokenGetter,
    accessToken,
  );
}

function normalizeWalletAddress(address: string): string {
  return address.startsWith("0x") ? address.toLowerCase() : address;
}

function normalizeSyntheticTarget(
  value: unknown,
): SyntheticDevAuthTarget | null {
  if (value === "local" || value === "staging") {
    return value;
  }

  return null;
}

function normalizeSyntheticVmPreference(value: unknown): VmPreference {
  if (value === "auto") {
    return "auto";
  }

  return parseVmId(value) ?? "auto";
}

export function normalizeSyntheticDevAuthProfile(
  value: unknown,
): SyntheticDevAuthProfile | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const candidate = value as Record<string, unknown>;
  const walletAddress = normalizeNonEmptyString(candidate.walletAddress);
  const target = normalizeSyntheticTarget(candidate.target);
  if (!walletAddress || !target) {
    return null;
  }

  return {
    devAuthToken: normalizeDevAuthToken(candidate.devAuthToken),
    evmPrivateKey: normalizeNonEmptyString(candidate.evmPrivateKey),
    secondaryWalletAddress: normalizeNonEmptyString(
      candidate.secondaryWalletAddress,
    ),
    target,
    userId: normalizeNonEmptyString(candidate.userId),
    vmPreference: normalizeSyntheticVmPreference(candidate.vmPreference),
    walletAddress: normalizeWalletAddress(walletAddress),
  };
}

function readStoredSyntheticProfile(): SyntheticDevAuthProfile | null {
  if (typeof window === "undefined" || !isSyntheticDevAuthRuntimeEnabled()) {
    return null;
  }

  const rawValue = window.localStorage.getItem(SYNTHETIC_DEV_AUTH_STORAGE_KEY);
  if (!rawValue) {
    return null;
  }

  try {
    const storedProfile = normalizeSyntheticDevAuthProfile(
      JSON.parse(rawValue),
    );
    return storedProfile
      ? {
          ...storedProfile,
          evmPrivateKey: readSessionSyntheticEvmPrivateKey(storedProfile),
        }
      : null;
  } catch {
    return null;
  }
}

function readSessionSyntheticEvmPrivateKey(
  profile: Pick<SyntheticDevAuthProfile, "target" | "userId" | "walletAddress">,
): string | null {
  if (typeof window === "undefined" || !isSyntheticDevAuthRuntimeEnabled()) {
    return null;
  }

  try {
    const rawValue = window.sessionStorage.getItem(
      SYNTHETIC_DEV_EVM_SIGNER_SESSION_STORAGE_KEY,
    );
    if (!rawValue) {
      return null;
    }

    const parsed: unknown = JSON.parse(rawValue);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return null;
    }

    const candidate = parsed as Record<string, unknown>;
    const evmPrivateKey = normalizeNonEmptyString(candidate.evmPrivateKey);
    const target = normalizeSyntheticTarget(candidate.target);
    const userId = normalizeNonEmptyString(candidate.userId);
    const walletAddress = normalizeNonEmptyString(candidate.walletAddress);
    if (!evmPrivateKey || !target || !walletAddress) {
      return null;
    }

    if (
      target !== profile.target ||
      userId !== profile.userId ||
      normalizeWalletAddress(walletAddress) !== profile.walletAddress
    ) {
      return null;
    }

    return evmPrivateKey;
  } catch {
    return null;
  }
}

function persistSessionSyntheticEvmPrivateKey(
  profile: SyntheticDevAuthProfile | null,
): void {
  if (typeof window === "undefined" || !isSyntheticDevAuthRuntimeEnabled()) {
    return;
  }

  try {
    if (!profile?.evmPrivateKey) {
      window.sessionStorage.removeItem(
        SYNTHETIC_DEV_EVM_SIGNER_SESSION_STORAGE_KEY,
      );
      return;
    }

    window.sessionStorage.setItem(
      SYNTHETIC_DEV_EVM_SIGNER_SESSION_STORAGE_KEY,
      JSON.stringify({
        evmPrivateKey: profile.evmPrivateKey,
        target: profile.target,
        userId: profile.userId,
        walletAddress: profile.walletAddress,
      }),
    );
  } catch {
    // Ignore storage failures; the in-memory profile still carries the signer.
  }
}

function persistSyntheticProfile(
  profile: SyntheticDevAuthProfile | null,
): void {
  if (typeof window === "undefined" || !isSyntheticDevAuthRuntimeEnabled()) {
    return;
  }

  if (!profile) {
    window.localStorage.removeItem(SYNTHETIC_DEV_AUTH_STORAGE_KEY);
    persistSessionSyntheticEvmPrivateKey(null);
    return;
  }

  persistSessionSyntheticEvmPrivateKey(profile);
  const persistedProfile: SyntheticDevAuthProfile = {
    ...profile,
    evmPrivateKey: null,
  };

  window.localStorage.setItem(
    SYNTHETIC_DEV_AUTH_STORAGE_KEY,
    JSON.stringify(persistedProfile),
  );
}

export function normalizeSiweAuthProfile(
  value: unknown,
): SiweAuthProfile | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const candidate = value as Record<string, unknown>;
  const walletAddress = normalizeNonEmptyString(candidate.walletAddress);
  const chainId = normalizePositiveInteger(candidate.chainId);
  if (
    !walletAddress ||
    !chainId ||
    !/^0x[0-9a-fA-F]{40}$/u.test(walletAddress)
  ) {
    return null;
  }

  const accessToken = normalizeSiweAccessToken(candidate.accessToken);
  const refreshToken = normalizeNonEmptyString(candidate.refreshToken);
  if (!accessToken && !refreshToken) {
    return null;
  }

  return {
    accessToken,
    chainId,
    expiresAt: normalizeIsoDateString(candidate.expiresAt),
    refreshToken,
    sessionId: normalizeNonEmptyString(candidate.sessionId),
    walletAddress: normalizeWalletAddress(walletAddress),
  };
}

export function normalizeSiweEvmSignerProfile(
  value: unknown,
): SiweEvmSignerProfile | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const candidate = value as Record<string, unknown>;
  const chainId = normalizePositiveInteger(candidate.chainId);
  const evmPrivateKey = normalizeNonEmptyString(candidate.evmPrivateKey);
  const walletAddress = normalizeNonEmptyString(candidate.walletAddress);
  if (
    !chainId ||
    !evmPrivateKey ||
    !/^0x[0-9a-fA-F]{64}$/u.test(evmPrivateKey) ||
    !walletAddress ||
    !/^0x[0-9a-fA-F]{40}$/u.test(walletAddress)
  ) {
    return null;
  }

  return {
    chainId,
    evmPrivateKey,
    sessionId: normalizeNonEmptyString(candidate.sessionId),
    walletAddress: normalizeWalletAddress(walletAddress),
  };
}

function readStoredSiweEvmSignerProfile(): SiweEvmSignerProfile | null {
  if (typeof window === "undefined" || !isSiweAuthRuntimeEnabled()) {
    return null;
  }

  try {
    const rawValue = window.sessionStorage.getItem(
      SIWE_EVM_SIGNER_SESSION_STORAGE_KEY,
    );
    if (!rawValue) {
      return null;
    }

    return normalizeSiweEvmSignerProfile(JSON.parse(rawValue));
  } catch {
    return null;
  }
}

function persistSiweEvmSignerProfile(
  profile: SiweEvmSignerProfile | null,
): void {
  if (typeof window === "undefined" || !isSiweAuthRuntimeEnabled()) {
    return;
  }

  try {
    if (!profile) {
      window.sessionStorage.removeItem(SIWE_EVM_SIGNER_SESSION_STORAGE_KEY);
      return;
    }

    window.sessionStorage.setItem(
      SIWE_EVM_SIGNER_SESSION_STORAGE_KEY,
      JSON.stringify(profile),
    );
  } catch {
    // Ignore storage failures; the in-memory profile still carries the signer.
  }
}

function persistSiweProfile(profile: SiweAuthProfile | null): void {
  if (typeof window === "undefined" || !isSiweAuthRuntimeEnabled()) {
    return;
  }

  if (!profile) {
    window.localStorage.removeItem(SIWE_AUTH_STORAGE_KEY);
    window.sessionStorage.removeItem(SIWE_AUTH_STORAGE_KEY);
    return;
  }

  // Refresh tokens rotate. Keeping the complete session tab-scoped prevents
  // independent tabs from replaying the same persisted refresh token.
  window.localStorage.removeItem(SIWE_AUTH_STORAGE_KEY);
  window.sessionStorage.setItem(SIWE_AUTH_STORAGE_KEY, JSON.stringify(profile));
}

function readStoredSiweProfile(): SiweAuthProfile | null {
  if (typeof window === "undefined" || !isSiweAuthRuntimeEnabled()) {
    return null;
  }

  const rawValue = window.sessionStorage.getItem(SIWE_AUTH_STORAGE_KEY);
  if (!rawValue) {
    return null;
  }

  try {
    return normalizeSiweAuthProfile(JSON.parse(rawValue));
  } catch {
    return null;
  }
}

function ensureSiweEvmSignerProfileInitialized(): void {
  if (siweEvmSignerProfileInitialized) {
    return;
  }

  siweEvmSignerProfileInitialized = true;
  siweEvmSignerProfile = readStoredSiweEvmSignerProfile();
}

function ensureSiweProfileInitialized(): void {
  if (siweProfileInitialized) {
    return;
  }

  siweProfileInitialized = true;
  siweProfile = readStoredSiweProfile();
  if (siweProfile) {
    persistSiweProfile(siweProfile);
  }
}

function readSearchSyntheticProfile(): SyntheticDevAuthProfile | null {
  if (typeof window === "undefined" || !isSyntheticDevAuthRuntimeEnabled()) {
    return null;
  }

  const searchParams = new URLSearchParams(window.location.search);
  const walletAddress = normalizeNonEmptyString(
    searchParams.get(QUERY_PARAM_WALLET),
  );
  const target = normalizeSyntheticTarget(searchParams.get(QUERY_PARAM_TARGET));
  if (!walletAddress || !target) {
    return null;
  }

  return normalizeSyntheticDevAuthProfile({
    secondaryWalletAddress: searchParams.get(QUERY_PARAM_SECONDARY_WALLET),
    target,
    userId: searchParams.get(QUERY_PARAM_USER_ID),
    vmPreference: searchParams.get(QUERY_PARAM_VM),
    walletAddress,
  });
}

function resolveSyntheticProfileBootstrap(): SyntheticDevAuthProfile | null {
  const storedProfile = readStoredSyntheticProfile();
  const searchProfile = readSearchSyntheticProfile();
  if (!searchProfile) {
    if (storedProfile) {
      persistSyntheticProfile(storedProfile);
    }
    return storedProfile;
  }

  const shouldReuseStoredToken =
    storedProfile &&
    storedProfile.target === searchProfile.target &&
    storedProfile.userId === searchProfile.userId &&
    storedProfile.vmPreference === searchProfile.vmPreference &&
    storedProfile.walletAddress === searchProfile.walletAddress;
  const resolvedProfile: SyntheticDevAuthProfile = {
    ...searchProfile,
    devAuthToken: shouldReuseStoredToken ? storedProfile.devAuthToken : null,
    evmPrivateKey: shouldReuseStoredToken ? storedProfile.evmPrivateKey : null,
  };

  persistSyntheticProfile(resolvedProfile);

  if (searchProfile.vmPreference !== "auto") {
    setVmPreference(searchProfile.vmPreference);
  }

  return resolvedProfile;
}

function ensureSyntheticProfileInitialized(): void {
  if (syntheticProfileInitialized) {
    return;
  }

  syntheticProfileInitialized = true;
  syntheticProfile = resolveSyntheticProfileBootstrap();
}

function areTransportSnapshotsEqual(
  left: AppAuthTransportSnapshot,
  right: AppAuthTransportSnapshot,
): boolean {
  return (
    left.mode === right.mode &&
    left.devAuthToken === right.devAuthToken &&
    left.evmAddress === right.evmAddress &&
    (left.siweAccessToken ?? null) === (right.siweAccessToken ?? null) &&
    left.solanaAddress === right.solanaAddress &&
    left.userId === right.userId &&
    left.walletAddress === right.walletAddress
  );
}

export function getSyntheticDevAuthProfile(): SyntheticDevAuthProfile | null {
  ensureSyntheticProfileInitialized();
  return syntheticProfile;
}

export function setSyntheticDevAuthProfile(
  profile: SyntheticDevAuthProfile | null,
): void {
  if (!isSyntheticDevAuthRuntimeEnabled()) {
    warnSyntheticDevAuthRuntimeDisabled();
    return;
  }

  ensureSyntheticProfileInitialized();
  const currentProfile = syntheticProfile;
  if (
    currentProfile &&
    profile &&
    JSON.stringify(currentProfile) === JSON.stringify(profile)
  ) {
    return;
  }

  persistSyntheticProfile(profile);
  if (profile) {
    setVmPreference(profile.vmPreference);
  }
  syntheticProfile = profile;
  emitListeners(syntheticProfileListeners);
}

export function activateSyntheticDevAuthProfile(
  input: Omit<SyntheticDevAuthProfile, "devAuthToken" | "evmPrivateKey"> & {
    devAuthToken?: string | null;
    evmPrivateKey?: string | null;
  },
): SyntheticDevAuthProfile | null {
  if (!isSyntheticDevAuthRuntimeEnabled()) {
    warnSyntheticDevAuthRuntimeDisabled();
    return null;
  }

  const normalizedProfile = normalizeSyntheticDevAuthProfile({
    ...input,
    devAuthToken: input.devAuthToken ?? null,
    evmPrivateKey: input.evmPrivateKey ?? null,
  });
  if (!normalizedProfile) {
    return null;
  }

  setSyntheticDevAuthProfile(normalizedProfile);
  return normalizedProfile;
}

export function updateSyntheticDevAuthProfile(
  patch: SyntheticDevAuthProfilePatch,
): SyntheticDevAuthProfile | null {
  if (!isSyntheticDevAuthRuntimeEnabled()) {
    warnSyntheticDevAuthRuntimeDisabled();
    return null;
  }

  const currentProfile = getSyntheticDevAuthProfile();
  const nextProfile = normalizeSyntheticDevAuthProfile(
    currentProfile
      ? {
          ...currentProfile,
          ...patch,
        }
      : {
          ...patch,
          target: normalizeSyntheticTarget(patch.target) ?? "local",
        },
  );
  if (!nextProfile) {
    return currentProfile;
  }

  setSyntheticDevAuthProfile(nextProfile);
  return nextProfile;
}

export function clearSyntheticDevAuthProfile(): void {
  setSyntheticDevAuthProfile(null);
}

export function subscribeSyntheticDevAuthProfile(
  listener: Listener,
): () => void {
  ensureSyntheticProfileInitialized();
  return subscribeToSet(syntheticProfileListeners, listener);
}

export function useSyntheticDevAuthProfile(): SyntheticDevAuthProfile | null {
  return useSyncExternalStore(
    subscribeSyntheticDevAuthProfile,
    getSyntheticDevAuthProfile,
    () => null,
  );
}

export function getSiweAuthProfile(): SiweAuthProfile | null {
  ensureSiweProfileInitialized();
  return siweProfile;
}

export function setSiweAuthProfile(profile: SiweAuthProfile | null): void {
  ensureSiweProfileInitialized();
  const currentProfile = siweProfile;
  if (
    currentProfile &&
    profile &&
    JSON.stringify(currentProfile) === JSON.stringify(profile)
  ) {
    return;
  }

  persistSiweProfile(profile);
  siweProfile = profile;
  if (!profile) {
    setSiweEvmSignerProfile(null);
  }
  emitListeners(siweProfileListeners);
}

export function activateSiweAuthProfile(
  input: SiweAuthProfile,
): SiweAuthProfile | null {
  const normalizedProfile = normalizeSiweAuthProfile(input);
  if (!normalizedProfile) {
    return null;
  }

  setSiweAuthProfile(normalizedProfile);
  return normalizedProfile;
}

export function updateSiweAuthProfile(
  patch: SiweAuthProfilePatch,
): SiweAuthProfile | null {
  const currentProfile = getSiweAuthProfile();
  const nextProfile = normalizeSiweAuthProfile(
    currentProfile
      ? {
          ...currentProfile,
          ...patch,
        }
      : patch,
  );
  if (!nextProfile) {
    return currentProfile;
  }

  setSiweAuthProfile(nextProfile);
  return nextProfile;
}

export function clearSiweAuthProfile(): void {
  setSiweAuthProfile(null);
}

export function subscribeSiweAuthProfile(listener: Listener): () => void {
  ensureSiweProfileInitialized();
  return subscribeToSet(siweProfileListeners, listener);
}

export function useSiweAuthProfile(): SiweAuthProfile | null {
  return useSyncExternalStore(
    subscribeSiweAuthProfile,
    getSiweAuthProfile,
    () => null,
  );
}

export function getSiweEvmSignerProfile(): SiweEvmSignerProfile | null {
  ensureSiweEvmSignerProfileInitialized();
  return siweEvmSignerProfile;
}

export function setSiweEvmSignerProfile(
  profile: SiweEvmSignerProfile | null,
): void {
  ensureSiweEvmSignerProfileInitialized();
  const normalizedProfile = profile
    ? normalizeSiweEvmSignerProfile(profile)
    : null;
  const currentProfile = siweEvmSignerProfile;
  if (JSON.stringify(currentProfile) === JSON.stringify(normalizedProfile)) {
    return;
  }

  persistSiweEvmSignerProfile(normalizedProfile);
  siweEvmSignerProfile = normalizedProfile;
  emitListeners(siweEvmSignerProfileListeners);
}

export function subscribeSiweEvmSignerProfile(listener: Listener): () => void {
  ensureSiweEvmSignerProfileInitialized();
  return subscribeToSet(siweEvmSignerProfileListeners, listener);
}

export function useSiweEvmSignerProfile(): SiweEvmSignerProfile | null {
  return useSyncExternalStore(
    subscribeSiweEvmSignerProfile,
    getSiweEvmSignerProfile,
    () => null,
  );
}

export function getAppAuthTransportSnapshot(): AppAuthTransportSnapshot {
  return transportSnapshot;
}

export function setAppAuthTransportSnapshot(
  snapshot: AppAuthTransportSnapshot,
): void {
  if (areTransportSnapshotsEqual(transportSnapshot, snapshot)) {
    return;
  }

  transportSnapshot = snapshot;
  clearCapabilityTokenCache();
  emitListeners(transportSnapshotListeners);
}

export async function getAppAuthTransportAuthHeadersForSnapshot(
  snapshot: AppAuthTransportSnapshot,
): Promise<Record<string, string>> {
  switch (snapshot.mode) {
    case "privy": {
      const getter =
        privyAccessTokenGetter ?? (await waitForPrivyAccessTokenGetter());
      const normalizedToken = await readPrivyAccessToken(getter, snapshot);
      const headers: Record<string, string> = {};
      if (normalizedToken) {
        headers.Authorization = `Bearer ${normalizedToken}`;
      }
      const identityToken = await readPrivyIdentityTokenBestEffort(
        privyIdentityTokenGetter,
        normalizedToken,
      );
      if (identityToken) {
        headers["privy-id-token"] = identityToken;
      }
      return headers;
    }
    case "synthetic-local":
    case "synthetic-staging":
      return snapshot.devAuthToken
        ? { "x-glider-dev-auth-token": snapshot.devAuthToken }
        : {};
    case "siwe":
      return snapshot.siweAccessToken
        ? { Authorization: `Bearer ${snapshot.siweAccessToken}` }
        : {};
    default:
      return {};
  }
}

export async function getAppAuthCapabilityTokenForSnapshot(
  snapshot: AppAuthTransportSnapshot,
  request?: AppAuthCapabilityTokenRequest,
): Promise<string | null> {
  return await readCapabilityTokenForSnapshot(
    snapshot,
    normalizeCapabilityTokenRequest(request),
  );
}

export async function getAppAuthCapabilityToken(
  request?: AppAuthCapabilityTokenRequest,
): Promise<string | null> {
  const snapshot = getAppAuthTransportSnapshot();
  const token = await getAppAuthCapabilityTokenForSnapshot(snapshot, request);
  return areTransportSnapshotsEqual(getAppAuthTransportSnapshot(), snapshot)
    ? token
    : null;
}

export async function refreshAppAuthCapabilityToken(
  request?: AppAuthCapabilityTokenRequest,
): Promise<string | null> {
  const snapshot = getAppAuthTransportSnapshot();
  const token = await readCapabilityTokenForSnapshot(
    snapshot,
    normalizeCapabilityTokenRequest(request),
    { forceRefresh: true },
  );
  return areTransportSnapshotsEqual(getAppAuthTransportSnapshot(), snapshot)
    ? token
    : null;
}

export function subscribeAppAuthTransportSnapshot(
  listener: Listener,
): () => void {
  return subscribeToSet(transportSnapshotListeners, listener);
}

export function registerPrivyAccessTokenGetter(
  getter: PrivyAccessTokenGetter,
): () => void {
  privyAccessTokenGetter = getter;
  clearPrivyAccessTokenCache();
  clearCapabilityTokenCache();
  resolvePrivyAccessTokenGetterWaiters(getter);

  return () => {
    if (privyAccessTokenGetter === getter) {
      privyAccessTokenGetter = null;
      clearPrivyAccessTokenCache();
      clearCapabilityTokenCache();
    }
  };
}

export function registerPrivyIdentityTokenGetter(
  getter: PrivyIdentityTokenGetter,
): () => void {
  privyIdentityTokenGetter = getter;

  return () => {
    if (privyIdentityTokenGetter === getter) {
      privyIdentityTokenGetter = null;
    }
  };
}

export async function getAppAuthTransportAuthHeaders(): Promise<
  Record<string, string>
> {
  const snapshot = getAppAuthTransportSnapshot();

  if (snapshot.mode !== "privy") {
    return await getAppAuthTransportAuthHeadersForSnapshot(snapshot);
  }

  const headers = await getAppAuthTransportAuthHeadersForSnapshot(snapshot);
  return areTransportSnapshotsEqual(getAppAuthTransportSnapshot(), snapshot)
    ? headers
    : {};
}

export function resetAppAuthRuntimeForTests(): void {
  syntheticProfileInitialized = false;
  syntheticProfile = null;
  siweProfileInitialized = false;
  siweProfile = null;
  siweEvmSignerProfileInitialized = false;
  siweEvmSignerProfile = null;
  transportSnapshot = GUEST_TRANSPORT_SNAPSHOT;
  privyAccessTokenGetter = null;
  privyIdentityTokenGetter = null;
  clearPrivyAccessTokenCache();
  clearPrivyIdentityTokenCacheForTests();
  clearCapabilityTokenCache();
  resolvePrivyAccessTokenGetterWaiters(null);
  emitListeners(syntheticProfileListeners);
  emitListeners(siweProfileListeners);
  emitListeners(siweEvmSignerProfileListeners);
  emitListeners(transportSnapshotListeners);
}

export function getSyntheticWalletAddresses(profile: SyntheticDevAuthProfile): {
  evmAddress: string | null;
  solanaAddress: string | null;
} {
  const addresses = [
    profile.walletAddress,
    profile.secondaryWalletAddress,
  ].filter((value): value is string => Boolean(value));

  let evmAddress: string | null = null;
  let solanaAddress: string | null = null;

  for (const address of addresses) {
    const vmId = normalizeVmId(undefined, address);
    if (vmId === "evm" && !evmAddress) {
      evmAddress = normalizeWalletAddress(address);
    }
    if (vmId === "solana" && !solanaAddress) {
      solanaAddress = address;
    }
  }

  return {
    evmAddress,
    solanaAddress,
  };
}
